import { getZar8UnlockPositionLockedAmount } from "../utils/zar8LockFlow";
import { ApiError, create, get, axiosInstance } from "./apiService";

const ZAR8_LOCK_PERIOD_BY_DURATION_ID = {
  flex: "flex",
  "6m": "sixMonths",
  "12m": "twelveMonths",
  "3y": "threeYears",
  "5y": "fiveYears",
};

export const normalizeZar8TwoFactorCode = ({
  twoFactorCode,
  twoFactorToken,
} = {}) => twoFactorCode ?? twoFactorToken ?? "";

export const buildZar8TwoFactorBody = (code) => ({
  twoFactorCode: code,
  otpCode: code,
  twoFaCode: code,
});

export const buildZar8TwoFactorConfig = (code, baseConfig) => {
  if (!code) {
    return baseConfig;
  }
  const baseHeaders = baseConfig?.headers ?? {};
  return {
    ...(baseConfig || {}),
    headers: {
      ...baseHeaders,
      "X-2FA-Code": code,
    },
  };
};

const postZar8WithEnvelope = async (url, body, code, extraConfig) => {
  let response;
  try {
    response = await axiosInstance.post(
      url,
      body,
      buildZar8TwoFactorConfig(code, extraConfig)
    );
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (data && typeof data === "object") {
      throw new ApiError(
        data.message || data.error || err?.message || "Request failed",
        status,
        data
      );
    }
    throw err;
  }

  const envelope = response?.data;
  if (envelope && typeof envelope === "object") {
    if (envelope.status === false || envelope.success === false) {
      throw new ApiError(
        envelope.message || envelope.error || "Request failed",
        response.status,
        envelope
      );
    }
    return envelope.data ?? envelope;
  }
  return envelope;
};

export const getZar8YieldPositions = (params = {}) =>
  get("/api/v2/zar8/positions", params);

export const getZar8YieldPosition = (positionId) =>
  get(`/api/v2/zar8/positions/${positionId}`);

export const getZar8YieldHistory = (params = {}) =>
  get("/api/v2/zar8/yield/history", params);

export const getZar8YieldEarnings = () => get("/api/v2/zar8/yield/earnings");

export const getZar8YieldConfig = () => get("/api/v2/zar8/yield-config");

export const calculateZar8Yield = (params = {}) =>
  get("/api/v2/zar8/yield/calculate", params);

export const createZar8YieldPosition = (payload, config) =>
  create("/api/v2/zar8/positions", payload, config);

export const createZar8Lock = async ({
  amount,
  durationId,
  yieldAssetId,
  accountId,
  twoFactorCode,
  twoFactorToken,
}) => {
  if (!ZAR8_LOCK_PERIOD_BY_DURATION_ID[durationId]) {
    throw new Error("Select a valid lock duration to continue.");
  }

  const code = twoFactorCode ?? twoFactorToken ?? "";
  const body = {
    amount,
    durationId,
    accountId,
    yieldAssetId,
    twoFactorCode: code,
    otpCode: code,
    twoFaCode: code,
  };

  const requestConfig =
    code !== ""
      ? {
          headers: {
            "X-2FA-Code": code,
          },
        }
      : undefined;

  return create("/api/v2/zar8/yield/lock", body, requestConfig);
};

export const createZar8UnlockQuote = async ({
  amountZar,
  selectedPosition,
}) => {
  const lockedAmountZar = getZar8UnlockPositionLockedAmount(selectedPosition);

  if (!selectedPosition) {
    throw new Error("Select an active yield term to continue.");
  }

  if (amountZar <= 0) {
    throw new Error("Enter a valid amount to continue.");
  }

  if (amountZar > lockedAmountZar) {
    throw new Error("Amount exceeds your locked ZAR8 balance.");
  }

  return create(
    `/api/v2/zar8/yield/positions/${selectedPosition.ledgerId}/unlock-quote`,
    {
      amount: amountZar,
      zar8Amount: amountZar,
    }
  );
};

export const getZar8UnlockErrorEnvelope = (error) => {
  if (!error || typeof error !== "object") return null;
  const r = error.response;
  if (!r || typeof r !== "object") return null;
  if (
    typeof r.success === "boolean" ||
    typeof r.code === "string" ||
    (typeof r.message === "string" && "success" in r)
  ) {
    return r;
  }
  const data = r.data;
  if (
    data &&
    typeof data === "object" &&
    (typeof data.success === "boolean" || typeof data.code === "string")
  ) {
    return data;
  }
  return null;
};

export const createZar8Unlock = async ({
  amountZar,
  selectedPosition,
  twoFactorCode,
  twoFactorToken,
  quote,
}) => {
  const lockedAmountZar = getZar8UnlockPositionLockedAmount(selectedPosition);

  if (!selectedPosition) {
    throw new Error("Select an active yield term to continue.");
  }

  if (amountZar <= 0 || amountZar > lockedAmountZar) {
    throw new Error("Unable to submit this unlock request.");
  }

  const code = twoFactorCode ?? twoFactorToken ?? "";
  const body = {
    zar8Amount: amountZar,
    amount: amountZar,
    quote,
    twoFactorCode: code,
    otpCode: code,
    twoFaCode: code,
  };

  let response;
  try {
    response = await axiosInstance.post(
      `/api/v2/zar8/positions/${selectedPosition.ledgerId}/unlock`,
      body,
      {
        headers: {
          "X-2FA-Code": code,
        },
      }
    );
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (data && typeof data === "object") {
      throw new ApiError(
        data.message || data.error || err?.message || "Unlock failed",
        status,
        data
      );
    }
    throw err;
  }

  const envelope = response?.data;
  if (envelope && typeof envelope === "object") {
    if (envelope.status === false || envelope.success === false) {
      throw new ApiError(
        envelope.message || envelope.error || "Unlock failed",
        response.status,
        envelope
      );
    }
    return envelope.data ?? envelope;
  }
  return envelope;
};

export const getZar8YieldPositionsList = (params = {}) =>
  get("/api/v2/zar8/yield/positions", params);

export const getZar8YieldPositionById = (positionId) => {
  if (!positionId) {
    throw new Error("A positionId is required.");
  }
  return get(`/api/v2/zar8/yield/positions/${encodeURIComponent(positionId)}`);
};

export const getZar8YieldPositionDistributions = (positionId, params = {}) => {
  if (!positionId) {
    throw new Error("A positionId is required.");
  }
  return get(
    `/api/v2/zar8/yield/positions/${encodeURIComponent(positionId)}/distributions`,
    params
  );
};

export const getZar8YieldConfigSettings = () =>
  get("/api/v2/zar8/yield/config");

export const createZar8YieldPositionV2 = async ({
  zar8Amount = "",
  amount = "",
  lockPeriod = "",
  durationId = "",
  accountId = "",
  twoFactorCode,
  twoFactorToken,
} = {}) => {
  const code = normalizeZar8TwoFactorCode({ twoFactorCode, twoFactorToken });
  const body = {
    zar8Amount,
    amount,
    lockPeriod,
    durationId,
    accountId,
    ...buildZar8TwoFactorBody(code),
  };
  return create(
    "/api/v2/zar8/yield/positions",
    body,
    buildZar8TwoFactorConfig(code)
  );
};

export const cancelZar8YieldPosition = async ({
  positionId,
  twoFactorCode,
  twoFactorToken,
} = {}) => {
  if (!positionId) {
    throw new Error("Select a yield position to cancel.");
  }
  const code = normalizeZar8TwoFactorCode({ twoFactorCode, twoFactorToken });
  return postZar8WithEnvelope(
    `/api/v2/zar8/yield/positions/${encodeURIComponent(positionId)}/cancel`,
    buildZar8TwoFactorBody(code),
    code
  );
};

export const cancelZar8Position = async ({
  positionId,
  twoFactorCode,
  twoFactorToken,
} = {}) => {
  if (!positionId) {
    throw new Error("Select a position to cancel.");
  }
  const code = normalizeZar8TwoFactorCode({ twoFactorCode, twoFactorToken });
  return postZar8WithEnvelope(
    `/api/v2/zar8/positions/${encodeURIComponent(positionId)}/cancel`,
    buildZar8TwoFactorBody(code),
    code
  );
};

export const createZar8YieldPositionUnlock = async ({
  amountZar,
  selectedPosition,
  twoFactorCode,
  twoFactorToken,
  quote,
} = {}) => {
  if (!selectedPosition) {
    throw new Error("Select an active yield term to continue.");
  }

  const lockedAmountZar = getZar8UnlockPositionLockedAmount(selectedPosition);
  if (amountZar <= 0 || amountZar > lockedAmountZar) {
    throw new Error("Unable to submit this unlock request.");
  }

  const code = normalizeZar8TwoFactorCode({ twoFactorCode, twoFactorToken });
  const body = {
    zar8Amount: amountZar,
    amount: amountZar,
    quote,
    ...buildZar8TwoFactorBody(code),
  };
  return postZar8WithEnvelope(
    `/api/v2/zar8/yield/positions/${encodeURIComponent(
      selectedPosition.ledgerId
    )}/unlock`,
    body,
    code
  );
};
